import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Landing page pública do FalaVipPay.
 * Tese: "o banco, direto no seu sistema". Assinatura: o comprovante + dados em mono.
 * Self-contained (estilos num <style> escopado em .lp). Mobile-first, sem overflow-x.
 */

const WHATS = 'https://wa.me/5537991265314?text=Quero%20conhecer%20o%20FalaVipPay';
const PHONE = '(37) 9 9126-5314';

const FONTS = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap';

const features = [
  { k: 'PIX', t: 'Cobrar e receber', d: 'QR Code, copia-e-cola e cobrança com vencimento. Cai direto na conta, conciliado na hora.' },
  { k: 'PIX', t: 'Pagar por chave', d: 'Envie PIX por chave (CPF, CNPJ, telefone, e-mail ou aleatória) com confirmação em 2 passos.' },
  { k: 'Boleto', t: 'Emitir e receber', d: 'Gere boletos da sua conta, acompanhe baixas e movimentações — sem taxa de gateway.' },
  { k: 'Boleto', t: 'Pagar e DDA', d: 'Pague boletos por código de barras e consulte seus DDA direto pela plataforma.' },
  { k: 'TED', t: 'Transferências', d: 'TED e transferências entre contas com a segurança do certificado A1 ICP-Brasil.' },
  { k: 'BI', t: 'Relatórios & extratos', d: 'Extrato detalhado, fluxo de caixa por mês/categoria/dia e gráficos — exporta em CSV e PDF.' },
  { k: 'Avisos', t: 'WhatsApp e e-mail', d: 'Comprovantes e notificações no WhatsApp ou por e-mail. Você escolhe o canal.' },
];

// Mini fluxo de caixa (entradas/saídas) — ilustrativo
const chart = [
  { m: 'Fev', inn: 62, out: 38 }, { m: 'Mar', inn: 74, out: 52 }, { m: 'Abr', inn: 58, out: 44 },
  { m: 'Mai', inn: 88, out: 60 }, { m: 'Jun', inn: 96, out: 55 }, { m: 'Jul', inn: 80, out: 48 },
];

const steps = [
  { n: '01', t: 'Conecte sua conta Sicoob', d: 'Suba seu certificado A1 e configure o CSC. Em minutos a conta está integrada.' },
  { n: '02', t: 'Cobre, pague, integre', d: 'Use o painel ou a API. Seu ERP, e-commerce ou WhatsApp passam a falar com o banco.' },
  { n: '03', t: 'Aprove e comprove', d: 'Aprove pagamentos pelo WhatsApp e o comprovante chega sozinho no fornecedor.' },
];

const LandingPage: React.FC = () => {
  useEffect(() => {
    if (!document.getElementById('lp-fonts')) {
      const l = document.createElement('link');
      l.id = 'lp-fonts'; l.rel = 'stylesheet'; l.href = FONTS;
      document.head.appendChild(l);
    }

    // O app-shell trava html/#root (overflow:hidden / 100vh) p/ o dashboard.
    // Aqui a página é pública e longa: liberamos o scroll na raiz e restauramos ao sair.
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow, htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow, bodyHeight: body.style.height, bodyPosition: body.style.position,
      rootHeight: root?.style.height, rootOverflow: root?.style.overflow, rootMinH: root?.style.minHeight,
    };
    html.style.overflow = 'visible'; html.style.height = 'auto';
    body.style.overflow = 'visible'; body.style.overflowX = 'hidden'; body.style.height = 'auto'; body.style.position = 'static';
    if (root) { root.style.height = 'auto'; root.style.minHeight = '100vh'; root.style.overflow = 'visible'; }

    return () => {
      html.style.overflow = prev.htmlOverflow; html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow; body.style.height = prev.bodyHeight; body.style.position = prev.bodyPosition;
      if (root) { root.style.height = prev.rootHeight || ''; root.style.minHeight = prev.rootMinH || ''; root.style.overflow = prev.rootOverflow || ''; }
    };
  }, []);

  return (
    <div className="lp">
      <style>{`
        html, body { max-width: 100%; overflow-x: hidden; }
        .lp { background:#06262B; color:#EAF2F0; font-family:'Inter',system-ui,sans-serif; width:100%; max-width:100%; overflow-x:hidden; }
        .lp * { box-sizing:border-box; }
        .lp .disp { font-family:'Space Grotesk',sans-serif; letter-spacing:-.02em; }
        .lp .mono { font-family:'JetBrains Mono',monospace; }
        .lp .wrap { max-width:1120px; margin:0 auto; padding-left:20px; padding-right:20px; }
        .lp a { text-decoration:none; }
        @keyframes lpDash { to { stroke-dashoffset:-240; } }
        @keyframes lpRise { from { opacity:0; transform:translateY(16px);} to { opacity:1; transform:none; } }
        .lp .rise { animation:lpRise .7s cubic-bezier(.2,.7,.2,1) both; }

        /* nav */
        .lp nav.bar { display:flex; align-items:center; justify-content:space-between; padding-top:18px; padding-bottom:18px; }
        .lp .navlinks { display:flex; align-items:center; gap:18px; font-size:14px; }
        .lp .navlinks a.ghost { color:#9FB6B3; }
        .lp .btn { display:inline-block; font-weight:700; border-radius:11px; padding:11px 18px; cursor:pointer; transition:transform .15s ease, box-shadow .15s ease, background .15s ease; }
        .lp .btn:hover { transform:translateY(-2px); }
        .lp .btn-lime { background:#7DD957; color:#06262B; }
        .lp .btn-teal { background:#00AE9D; color:#06262B; box-shadow:0 10px 26px rgba(0,174,157,.3); }
        .lp .btn-teal:hover { box-shadow:0 14px 34px rgba(0,174,157,.42); }
        .lp .btn-ghost { border:1px solid rgba(255,255,255,.18); color:#EAF2F0; }
        .lp .btn-dark { background:#06262B; color:#EAF2F0; }

        /* hero */
        .lp .hero { position:relative; }
        .lp .hero .glow { position:absolute; inset:0; background:radial-gradient(900px 420px at 80% 6%, rgba(0,174,157,.20), transparent 60%), radial-gradient(700px 400px at 6% 34%, rgba(125,217,87,.10), transparent 60%); pointer-events:none; }
        .lp .hero-grid { position:relative; display:grid; grid-template-columns:1.1fr .9fr; gap:36px; align-items:center; padding-top:36px; padding-bottom:64px; }
        .lp h1 { font-size:56px; line-height:1.02; margin:0; font-weight:700; }
        .lp .lead { color:#A9C0BD; font-size:18px; line-height:1.55; max-width:480px; margin:18px 0 26px; }
        .lp .ctarow { display:flex; gap:12px; flex-wrap:wrap; }
        .lp .eyebrow { color:#00AE9D; font-size:13px; letter-spacing:.06em; margin-bottom:16px; }

        /* receipt card */
        .lp .receipt { background:#F5F3EC; color:#06262B; width:100%; max-width:280px; border-radius:16px; padding:22px 20px; transform:rotate(3deg); box-shadow:0 30px 60px rgba(0,0,0,.45); margin:0 auto; }
        .lp .rrow { display:flex; justify-content:space-between; font-size:12px; padding:2px 0; }

        /* sections */
        .lp section { position:relative; }
        .lp .band { background:#0B343B; border-top:1px solid rgba(255,255,255,.06); border-bottom:1px solid rgba(255,255,255,.06); }
        .lp h2 { font-size:34px; margin:0 0 8px; font-weight:700; }
        .lp .sub { color:#9FB6B3; font-size:16px; margin:0 0 30px; }
        .lp .pad { padding-top:66px; padding-bottom:38px; }

        /* flow */
        .lp .flow { display:flex; align-items:center; justify-content:space-between; gap:16px; padding-top:30px; padding-bottom:30px; }
        .lp .flow .node { text-align:center; min-width:120px; }

        /* grid cards */
        .lp .grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(270px,1fr)); gap:16px; }
        .lp .card { background:rgba(255,255,255,.03); border:1px solid rgba(255,255,255,.08); border-radius:16px; padding:20px 18px; transition:transform .18s ease,border-color .18s ease; }
        .lp .card:hover { transform:translateY(-4px); border-color:#00AE9D; }
        .lp .card .kk { color:#00AE9D; font-size:12px; font-weight:700; }
        .lp .card .tt { font-weight:600; font-size:18px; margin:7px 0 6px; }
        .lp .card .dd { color:#9FB6B3; font-size:14.5px; line-height:1.5; }

        /* whatsapp highlight */
        .lp .zap { display:grid; grid-template-columns:1fr 1fr; gap:36px; align-items:center; }
        .lp .bubble { background:#0c3b34; border:1px solid rgba(125,217,87,.25); border-radius:18px 18px 18px 4px; padding:14px 16px; max-width:320px; }
        .lp .bubble + .bubble { margin-top:10px; }

        /* api code */
        .lp .api { display:grid; grid-template-columns:1fr 1fr; gap:34px; align-items:center; }
        .lp .terminal { background:#04191D; border:1px solid rgba(255,255,255,.1); border-radius:14px; padding:16px; box-shadow:0 20px 50px rgba(0,0,0,.4); min-width:0; }
        .lp .terminal pre { margin:0; font-size:12.5px; line-height:1.7; color:#CFE9E4; white-space:pre-wrap; overflow-wrap:anywhere; word-break:break-word; }

        /* reports / BI */
        .lp .reports { display:grid; grid-template-columns:1fr 1fr; gap:34px; align-items:center; }
        .lp .panel { background:#04191D; border:1px solid rgba(255,255,255,.1); border-radius:16px; padding:20px; box-shadow:0 20px 50px rgba(0,0,0,.4); }
        .lp .stats { display:flex; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
        .lp .stat .lab { font-size:11px; color:#8AA39F; }
        .lp .stat .val { font-size:20px; font-weight:700; }
        .lp .chart { display:flex; align-items:flex-end; gap:12px; height:130px; }
        .lp .col { flex:1; display:flex; flex-direction:column; align-items:center; gap:6px; }
        .lp .pair { display:flex; align-items:flex-end; gap:3px; height:104px; }
        .lp .b { width:9px; border-radius:3px 3px 0 0; }
        .lp .b.inn { background:#00AE9D; }
        .lp .b.out { background:#3a5a55; }
        .lp .col .ml { font-size:10px; color:#8AA39F; }

        /* steps */
        .lp .steps { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .lp .step .num { font-family:'JetBrains Mono',monospace; color:#00AE9D; font-size:13px; }

        /* cta */
        .lp .cta { background:linear-gradient(120deg,#00AE9D,#00897B); color:#06262B; }
        .lp .cta .wrap { display:flex; align-items:center; justify-content:space-between; gap:24px; flex-wrap:wrap; padding-top:50px; padding-bottom:50px; }

        .lp footer { color:#7C9591; font-size:13.5px; }
        .lp footer .wrap { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; padding-top:26px; padding-bottom:26px; }
        .lp .contact { color:#EAF2F0; font-weight:600; }
        .lp .contact a { color:#7DD957; }

        /* Em telas de toque o :hover gruda e trava o scroll — neutraliza efeitos */
        @media (hover: none), (pointer: coarse) {
          .lp .card, .lp .btn { transition:none; }
          .lp .card:hover, .lp .btn:hover, .lp .btn-teal:hover { transform:none; }
          .lp .card:hover { border-color:rgba(255,255,255,.08); }
        }
        @media (max-width:860px){
          .lp h1 { font-size:38px; }
          .lp h2 { font-size:27px; }
          .lp .hero-grid { grid-template-columns:1fr; gap:28px; padding-bottom:48px; }
          .lp .zap, .lp .api { grid-template-columns:1fr; gap:24px; }
          .lp .flow { flex-direction:column; }
          .lp .flow svg { display:none; }
          .lp .steps { grid-template-columns:1fr; }
          .lp .pad { padding-top:48px; }
          .lp .navlinks a.ghost { display:none; }
          .lp .lead { font-size:16px; }
        }
        @media (prefers-reduced-motion: reduce){ .lp .rise, .lp .flow line { animation:none !important; } }
      `}</style>

      {/* NAV */}
      <header className="wrap">
        <nav className="bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: '#00AE9D', boxShadow: '0 0 0 4px rgba(0,174,157,.18)' }} />
            <span className="disp" style={{ fontWeight: 700, fontSize: 19 }}>FalaVipPay</span>
          </div>
          <div className="navlinks">
            <a className="ghost" href="#whatsapp">WhatsApp</a>
            <a className="ghost" href="#recursos">Recursos</a>
            <a className="ghost" href="#api">API</a>
            <RouterLink className="btn btn-lime" to="/login">Entrar</RouterLink>
          </div>
        </nav>
      </header>

      {/* HERO */}
      <section className="hero">
        <div className="glow" />
        <div className="wrap hero-grid">
          <div className="rise">
            <div className="eyebrow mono">// sem gateway. direto no banco.</div>
            <h1 className="disp">O banco, direto<br />no seu <span style={{ color: '#00AE9D' }}>sistema</span>.</h1>
            <p className="lead">
              PIX, boletos e transferências falando direto com o Sicoob — sem taxa de intermediário.
              Integre seu ERP por API, aprove pagamentos pelo WhatsApp e o comprovante chega sozinho.
            </p>
            <div className="ctarow">
              <RouterLink className="btn btn-teal" to="/login">Começar agora</RouterLink>
              <a className="btn btn-ghost" href="#whatsapp">Pagar pelo WhatsApp</a>
            </div>
          </div>
          <div className="rise" style={{ animationDelay: '.12s' }}>
            <div className="receipt">
              <div style={{ textAlign: 'center' }}>
                <span style={{ background: '#00AE9D', color: '#fff', fontWeight: 700, fontSize: 13, padding: '4px 14px', borderRadius: 20 }}>Sicoob</span>
              </div>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#0a8f3c', margin: '14px auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20 }}>✓</div>
              <div className="disp" style={{ textAlign: 'center', fontWeight: 700, fontSize: 15 }}>Comprovante de Pagamento</div>
              <div style={{ textAlign: 'center', fontSize: 11, color: '#5B6B6E' }}>Pagamento efetivado via Sicoob</div>
              <div className="disp" style={{ textAlign: 'center', fontSize: 30, fontWeight: 700, margin: '10px 0' }}>R$ 1.284,00</div>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '8px 0', paddingTop: 8 }} />
              {[['Forma', 'PIX (chave)'], ['Favorecido', 'Reval Atacado'], ['Data', 'hoje, 14:32']].map(([a, b]) => (
                <div className="rrow" key={a}><span style={{ color: '#5B6B6E' }}>{a}</span><span style={{ fontWeight: 600 }}>{b}</span></div>
              ))}
              <div className="mono" style={{ fontSize: 9.5, color: '#5B6B6E', marginTop: 10, overflowWrap: 'anywhere' }}>E2E E2555287532026…ORgKO3</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <div style={{ width: 38, height: 38, background: '#06262B', borderRadius: 6 }} />
                <span className="mono" style={{ fontSize: 11, color: '#00AE9D', fontWeight: 700 }}>A1B2-C3D4-E5F6</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FLUXO */}
      <section className="band">
        <div className="wrap flow">
          {[{ t: 'Seu sistema', s: 'ERP / loja / WhatsApp' }, { t: 'FalaVipPay', s: 'orquestra + assina' }, { t: 'Sicoob', s: 'banco, direto' }].map((n, i) => (
            <React.Fragment key={n.t}>
              <div className="node">
                <div className="disp" style={{ fontWeight: 700, fontSize: 18, color: i === 1 ? '#00AE9D' : '#EAF2F0' }}>{n.t}</div>
                <div style={{ fontSize: 12.5, color: '#8AA39F' }}>{n.s}</div>
              </div>
              {i < 2 && (
                <svg width="100%" height="24" style={{ flex: 1, maxWidth: 220 }} aria-hidden>
                  <line x1="0" y1="12" x2="100%" y2="12" stroke="#00AE9D" strokeWidth="2" strokeDasharray="8 8" style={{ animation: 'lpDash 6s linear infinite' }} />
                  <circle cx="100%" cy="12" r="3" fill="#7DD957" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* WHATSAPP — destaque */}
      <section id="whatsapp" className="wrap pad">
        <div className="zap">
          <div>
            <div className="eyebrow mono">// o seu banco no WhatsApp</div>
            <h2 className="disp">Pague PIX e boletos pelo WhatsApp</h2>
            <p className="sub" style={{ marginBottom: 18 }}>
              Mande o boleto ou a chave PIX num grupo, responda <b style={{ color: '#7DD957' }}>“aprovar”</b> e pronto: o FalaVipPay
              paga via Sicoob e devolve o comprovante. Um número, um grupo por empresa — você só troca quem participa.
            </p>
            <ul style={{ color: '#9FB6B3', fontSize: 15, lineHeight: 1.9, paddingLeft: 18, margin: 0 }}>
              <li>Aprovação com um toque, por quem você autorizar</li>
              <li>Comprovante em PDF (com QR de validação) automático</li>
              <li>Rastreável: cada pagamento fica registrado e auditável</li>
            </ul>
          </div>
          <div>
            <div className="bubble">
              <div style={{ fontSize: 13, color: '#CFE9E4' }}>💸 <b>Pagamento pendente</b> — R$ 1.284,00 · Reval Atacado<br /><span style={{ color: '#8AA39F' }}>Responda “aprovar” citando.</span></div>
            </div>
            <div className="bubble" style={{ marginLeft: 'auto', background: '#114b3f', borderRadius: '18px 18px 4px 18px', borderColor: 'rgba(125,217,87,.4)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#EAF2F0' }}>aprovar</div>
            </div>
            <div className="bubble">
              <div style={{ fontSize: 13, color: '#CFE9E4' }}>✅ <b>Aprovado e pago</b> — R$ 1.284,00<br /><span style={{ color: '#8AA39F' }}>Comprovante enviado ao fornecedor.</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* RECURSOS */}
      <section id="recursos" className="band">
        <div className="wrap pad">
          <h2 className="disp">Tudo que o dinheiro precisa</h2>
          <p className="sub">Uma plataforma, a conta do banco, zero gateway.</p>
          <div className="grid">
            {features.map((f) => (
              <div className="card" key={f.t}>
                <span className="kk mono">{f.k}</span>
                <div className="tt disp">{f.t}</div>
                <div className="dd">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATÓRIOS & BI */}
      <section className="wrap pad">
        <div className="reports">
          <div>
            <div className="eyebrow mono">// visão do seu dinheiro</div>
            <h2 className="disp">Relatórios que você entende</h2>
            <p className="sub">
              Extrato detalhado, saldo em tempo real e <b style={{ color: '#EAF2F0' }}>fluxo de caixa</b> agregado por mês,
              categoria e dia — com gráficos e exportação em CSV e PDF. As notificações chegam no WhatsApp ou por e-mail.
            </p>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', color: '#9FB6B3', fontSize: 13.5 }}>
              <span>📊 BI financeiro</span>
              <span>🧾 Extrato &amp; conciliação</span>
              <span>📥 Exporta CSV/PDF</span>
            </div>
          </div>
          <div className="panel">
            <div className="stats">
              <div className="stat"><div className="lab">Saldo</div><div className="val" style={{ color: '#7DD957' }}>R$ 42.180</div></div>
              <div className="stat"><div className="lab">Entradas (jun)</div><div className="val">R$ 96k</div></div>
              <div className="stat"><div className="lab">Saídas (jun)</div><div className="val" style={{ color: '#9FB6B3' }}>R$ 55k</div></div>
            </div>
            <div className="chart">
              {chart.map((c) => (
                <div className="col" key={c.m}>
                  <div className="pair">
                    <div className="b inn" style={{ height: `${c.inn}%` }} />
                    <div className="b out" style={{ height: `${c.out}%` }} />
                  </div>
                  <div className="ml">{c.m}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#8AA39F' }}>
              <span><span style={{ display: 'inline-block', width: 9, height: 9, background: '#00AE9D', borderRadius: 2, marginRight: 5 }} />Entradas</span>
              <span><span style={{ display: 'inline-block', width: 9, height: 9, background: '#3a5a55', borderRadius: 2, marginRight: 5 }} />Saídas</span>
            </div>
          </div>
        </div>
      </section>

      {/* API */}
      <section id="api" className="band"><div className="wrap pad">
        <div className="api">
          <div>
            <div className="eyebrow mono">// para desenvolvedores</div>
            <h2 className="disp">Cobre e pague por API</h2>
            <p className="sub">
              Uma chamada cria uma cobrança PIX ou envia um pagamento. Webhooks assinados (HMAC) avisam quando cai,
              e a inteligência concilia e classifica suas transações.
            </p>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', color: '#9FB6B3', fontSize: 13.5 }}>
              <span>🔐 Certificado A1 ICP-Brasil</span>
              <span>🔁 Webhooks HMAC</span>
              <span>🏢 Multi-empresa</span>
            </div>
          </div>
          <div className="terminal">
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {['#ff5f56', '#ffbd2e', '#27c93f'].map((c) => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
            </div>
            <pre>{`POST /api/v1/external/pix/charge
Authorization: X-API-Key ••••

{ "valor": "1284.00", "descricao": "NF 9261620" }

`}<span style={{ color: '#7DD957' }}>200 OK</span>{`
{ "txid": "e1b5…", "status": "ATIVA" }`}</pre>
          </div>
        </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="band">
        <div className="wrap pad">
          <h2 className="disp">Do certificado ao comprovante</h2>
          <p className="sub">Três passos pra tirar o intermediário do caminho.</p>
          <div className="steps">
            {steps.map((s) => (
              <div className="step card" key={s.n}>
                <div className="num">{s.n}</div>
                <div className="disp" style={{ fontWeight: 600, fontSize: 18, margin: '6px 0' }}>{s.t}</div>
                <div style={{ color: '#9FB6B3', fontSize: 14.5, lineHeight: 1.5 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + contato */}
      <section className="cta">
        <div className="wrap">
          <div>
            <h2 className="disp" style={{ color: '#06262B' }}>Pronto pra receber direto do banco?</h2>
            <p style={{ margin: '8px 0 0', color: '#063b35', fontSize: 16 }}>Fale comigo e conecte sua conta Sicoob — começa hoje.</p>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a className="btn btn-dark" href={WHATS} target="_blank" rel="noreferrer">Chamar no WhatsApp</a>
            <RouterLink className="btn" style={{ background: '#fff', color: '#06262B' }} to="/login">Entrar</RouterLink>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <span className="disp contact">FalaVipPay</span>
          <span>Gestão financeira com integração direta ao Sicoob</span>
          <span className="contact">Contato: <a href={WHATS} target="_blank" rel="noreferrer">{PHONE}</a></span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
